import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, MapPin, Clock, Users, ShoppingBag, Calendar, Star, AlertCircle, Lock } from 'lucide-react'
import { getEvent, getEventMenuItems, SecretEvent, SecretMenuItem } from '../lib/supabase'
import { useCart } from '../contexts/CartContext'
import { useEventCapacity } from '../hooks/useEventCapacity'
import { getEventStatus } from '../lib/utils/eventStatus'
import { getCapacityStatus } from '../lib/utils/capacityStatus'
import MenuGrid from '../components/menu/MenuGrid'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import CountdownTimer from '../components/ui/CountdownTimer'
import { FloatingCart } from '../components/cart/FloatingCart'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

const EventDetailsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { totalItems, totalAmount, setEventId } = useCart()

  const [event, setEvent] = useState<SecretEvent | null>(null)
  const [menuItems, setMenuItems] = useState<SecretMenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [menuLoading, setMenuLoading] = useState(true)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)

  // Phase 5: Use real-time capacity hook with 30-second polling
  const { capacity, loading: capacityLoading } = useEventCapacity(
    eventId || null,
    30000, // Poll every 30 seconds
    !!event?.max_lobster_tails // Only enable if event has capacity limits
  )

  useEffect(() => {
    if (!eventId) {
      navigate('/events')
      return
    }

    const loadEventData = async () => {
      try {
        setLoading(true)
        setMenuLoading(true)

        // Load event details and menu items in parallel
        const [eventData, menuData] = await Promise.all([
          getEvent(eventId),
          getEventMenuItems(eventId)
        ])

        if (!eventData) {
          toast.custom((t) => (
            <EnterpriseToast
              t={t}
              type="error"
              title="Event Not Found"
              message="This event could not be found"
            />
          ))
          navigate('/events')
          return
        }

        setEvent(eventData)

        // Deduplicate menu items by name and price (keep first occurrence)
        const uniqueMenuItems = menuData.filter((item, index, self) =>
          index === self.findIndex((t) => t.name === item.name && t.price === item.price)
        )

        setMenuItems(uniqueMenuItems)
        setEventId(eventId) // Set current event for cart
        // Phase 5: Capacity is now handled by useEventCapacity hook
      } catch (error) {
        console.error('Failed to load event data:', error)
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Loading Failed"
            message="Could not load event details. Please try again"
          />
        ))
      } finally {
        setLoading(false)
        setMenuLoading(false)
      }
    }

    loadEventData()
  }, [eventId, navigate, setEventId])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Phase 4: Urgency messaging based on capacity
  const getCapacityMessage = () => {
    if (!event?.max_lobster_tails || !capacity || capacity.is_unlimited) {
      return null
    }

    if (capacity.is_sold_out) {
      return {
        icon: '❌',
        text: 'Sold Out',
        color: 'text-red-500',
        bgColor: 'bg-red-500/10',
        borderColor: 'border-red-500'
      }
    }

    const remaining = capacity.remaining_capacity || 0
    const percentRemaining = capacity.percent_used !== null ? 100 - capacity.percent_used : 100

    if (percentRemaining > 25) {
      return {
        icon: '✅',
        text: 'Plenty available',
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        borderColor: 'border-green-500'
      }
    }

    return {
      icon: '🔥',
      text: `Almost sold out! Only ${remaining} tails left!`,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500'
    }
  }

  // Use calculated status instead of database status
  const calculatedStatus = event ? getEventStatus(event) : null
  const isEnrollmentOpen = calculatedStatus === 'Active'
  const isEventInProgress = calculatedStatus === 'In Progress'
  const canPlaceOrders = isEnrollmentOpen || isEventInProgress

  // When lobster tail capacity tracking is enabled, prioritize it over order count limits
  const hasAvailableSlots = event && (
    event.max_lobster_tails // If tail capacity is tracked, ignore max_orders
      ? true
      : (!event.max_orders || event.current_orders < event.max_orders)
  )
  const hasCapacityAvailable = !capacity?.is_sold_out

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📍</div>
          <h2 className="text-2xl font-bold text-secret-text-primary mb-2">Event Not Found</h2>
          <p className="text-secret-text-secondary mb-6">This event may no longer be available.</p>
          <button type="button" onClick={() => navigate('/events')} className="secret-btn-primary">
            Browse Events
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe bg-secret-bg-primary">
      {/* Hero Section - PRD Phase 1: Complete Overhaul */}
      <div
        className={`relative bg-cover bg-center transition-all duration-300 ${
          isDescriptionExpanded ? 'min-h-[500px] md:min-h-[600px]' : 'h-96 md:h-[500px]'
        }`}
        style={{
          backgroundImage: event.image_url
            ? `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55)), url(${event.image_url})`
            : 'linear-gradient(to bottom right, rgba(127, 29, 29, 0.3), rgba(127, 29, 29, 0.1))',
          filter: calculatedStatus === 'Completed' ? 'grayscale(50%)' : 'none'
        }}
      >
        {/* Cancelled State: Red Tint Overlay */}
        {calculatedStatus === 'Cancelled' && (
          <div className="absolute inset-0 bg-gradient-to-b from-red-600/30 to-red-600/50" />
        )}

        {/* Draft State: Warning Banner */}
        {calculatedStatus === 'Draft' && (
          <div className="absolute top-0 left-0 right-0 bg-yellow-500/90 text-gray-900 px-4 py-2 text-center font-semibold z-20">
            ⚠️ This is a preview and is not visible to the public
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

        {/* Flexbox Container - Mobile-First Restructure */}
        <div className="relative z-10 flex flex-col h-full p-4 sm:p-6 md:p-8">
          {/* Row 1: Header Controls */}
          <div className="flex justify-between items-start w-full mb-4">
            {/* Back Button - Modern Design */}
            <button
              type="button"
              onClick={() => navigate('/events')}
              className="flex items-center space-x-2 bg-white/95 hover:bg-white dark:bg-gray-900/95 dark:hover:bg-gray-900 text-gray-900 dark:text-gray-100 px-4 py-2.5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50"
            >
              <ArrowLeft
                size={18}
                strokeWidth={2.5}
                className="text-current"
              />
              <span className="font-semibold text-sm">Events</span>
            </button>

            {/* Status Badge - Modern, Elevated Design */}
            <div className={`px-4 sm:px-5 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wider shadow-lg backdrop-blur-sm border ${
              calculatedStatus === 'Active'
                ? 'bg-red-900/95 text-white border-red-800/50'
                : calculatedStatus === 'Scheduled'
                ? 'bg-red-900/95 text-white border-red-800/50'
                : calculatedStatus === 'In Progress'
                ? 'bg-red-900/95 text-white border-red-800/50'
                : calculatedStatus === 'Enrollment Closed'
                ? 'bg-yellow-50/95 text-yellow-800 border-yellow-200/50 dark:bg-yellow-900/95 dark:text-yellow-200 dark:border-yellow-800/50'
                : calculatedStatus === 'Completed'
                ? 'bg-gray-100/95 text-gray-600 border-gray-200/50 dark:bg-gray-800/95 dark:text-gray-400 dark:border-gray-700/50'
                : calculatedStatus === 'Cancelled'
                ? 'bg-red-600/95 text-white border-red-700/50'
                : calculatedStatus === 'Draft'
                ? 'bg-gray-200/95 text-gray-700 border-2 border-dashed border-gray-400/50'
                : 'bg-white/95 text-gray-700 border-gray-200/50 dark:bg-gray-900/95 dark:text-gray-300 dark:border-gray-700/50'
            }`}>
              {calculatedStatus === 'Active' ? 'Registration Open' :
               calculatedStatus === 'Scheduled' ? 'Opening Soon' :
               calculatedStatus === 'In Progress' ? 'In Progress' :
               calculatedStatus === 'Completed' ? 'Completed' :
               calculatedStatus === 'Cancelled' ? 'Cancelled' :
               calculatedStatus === 'Draft' ? 'Draft' :
               calculatedStatus}
            </div>
          </div>

          {/* Spacer - pushes content to center/bottom */}
          <div className="flex-grow" />

          {/* Hero Content - Modern Typography */}
          <div className="w-full max-w-7xl mx-auto px-0 sm:px-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-4 tracking-tight leading-tight text-center sm:text-left line-clamp-1">
              {event.title}
            </h1>
            <div className="max-w-3xl mx-auto sm:mx-0">
              <p className={`text-sm sm:text-base md:text-lg text-gray-200 leading-relaxed font-medium text-center sm:text-left transition-all duration-300 ${
                isDescriptionExpanded ? '' : 'line-clamp-4'
              }`}>
                {event.description}
              </p>
              {event.description && event.description.length > 250 && (
                <button
                  type="button"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="mt-2 text-white/90 hover:text-white text-sm font-semibold underline underline-offset-2 transition-colors"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
            <div className="mb-4 sm:mb-6" />

            {/* State-Specific Content */}
            <div className="space-y-4 flex flex-col items-center sm:items-start">
            {/* Scheduled State */}
            {calculatedStatus === 'Scheduled' && (
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4">
                <div className="flex-shrink-0">
                  <CountdownTimer
                    targetDate={new Date(event.registration_opens)}
                    label="Opens in"
                    className="text-white text-base font-semibold bg-white/10 backdrop-blur-sm px-8 py-5 rounded-2xl inline-flex items-center"
                  />
                </div>
              </div>
            )}

            {/* Active State */}
            {calculatedStatus === 'Active' && (
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4">
                <div className="flex-shrink-0">
                  <CountdownTimer
                    targetDate={new Date(event.registration_closes)}
                    label="Closes in"
                    className="text-white text-base font-semibold bg-white/10 backdrop-blur-sm px-8 py-5 rounded-2xl inline-flex items-center"
                  />
                </div>
                {hasAvailableSlots && hasCapacityAvailable && (
                  <div className="flex-grow sm:flex-grow-0">
                    <button
                      type="button"
                      onClick={() => {
                        const menuSection = document.querySelector('#menu-section')
                        menuSection?.scrollIntoView({ behavior: 'smooth' })
                      }}
                      className="w-full sm:w-auto bg-red-900 hover:bg-red-800 text-white px-10 py-5 rounded-2xl font-black text-lg transition-all duration-200 inline-flex items-center justify-center shadow-2xl hover:shadow-3xl hover:scale-105 border border-red-800/50"
                    >
                      Order Now
                      <ArrowLeft size={22} className="ml-3 rotate-180" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* In Progress State */}
            {calculatedStatus === 'In Progress' && event.pickup_end_time && (
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4">
                <div className="flex-shrink-0">
                  <CountdownTimer
                    targetDate={new Date(event.pickup_end_time)}
                    label="Event ends in"
                    className="text-white text-base font-semibold bg-white/10 backdrop-blur-sm px-8 py-5 rounded-2xl inline-flex items-center"
                  />
                </div>
                <div className="flex-grow sm:flex-grow-0">
                  <div className="w-full sm:w-auto bg-white/10 backdrop-blur-sm text-white px-10 py-5 rounded-2xl font-black text-lg inline-flex items-center justify-center">
                    Event is happening now! Pickup until {formatTime(event.pickup_end_time)}
                  </div>
                </div>
              </div>
            )}

            {/* Completed State */}
            {calculatedStatus === 'Completed' && (
              <>
                <p className="text-gray-200 text-lg font-semibold bg-gray-800/40 backdrop-blur-sm px-6 py-3 rounded-2xl inline-flex items-center border border-gray-600/30">
                  This event has ended
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/events')}
                  className="bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-200 inline-flex items-center border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  View Upcoming Events
                  <ArrowLeft size={18} className="ml-2 rotate-180" />
                </button>
              </>
            )}

            {/* Cancelled State */}
            {calculatedStatus === 'Cancelled' && (
              <>
                <p className="text-red-100 text-xl font-bold bg-red-900/40 backdrop-blur-sm px-6 py-4 rounded-2xl inline-flex items-center border border-red-500/30 shadow-lg">
                  This event has been cancelled
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/events')}
                  className="bg-red-800/90 hover:bg-red-700 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-200 inline-flex items-center border border-red-600/50 shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  View Other Events
                  <ArrowLeft size={18} className="ml-2 rotate-180" />
                </button>
              </>
            )}
            </div>
          </div>

          {/* Spacer - Optional bottom spacing */}
          <div className="hidden sm:block" style={{ minHeight: '2rem' }} />
        </div>
      </div>

      {/* PRD Phase 1: Event Details - 3 Column Card Grid */}
      {calculatedStatus !== 'Cancelled' && (
      <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Event Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {/* Location Card */}
          <div className="bg-card text-card-foreground rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-xl">
                <MapPin color="#7f1d1d" size={24} className="dark:text-secret-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Location</h3>
            </div>
            <div className="space-y-3">
              <p className="font-bold text-lg text-foreground">{event.location_name}</p>
              <p className="text-muted-foreground text-sm leading-relaxed">{event.address}</p>
            </div>
          </div>

          {/* Schedule Card */}
          <div className="bg-card text-card-foreground rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-xl">
                <Calendar color="#7f1d1d" size={24} className="dark:text-secret-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Schedule</h3>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Event Date</p>
                <p className="text-foreground font-bold text-base">
                  {formatDate(event.event_date)}
                </p>
                <p className="text-foreground font-semibold text-sm">
                  {event.pickup_start_time && event.pickup_end_time
                    ? `${formatTime(event.pickup_start_time)} - ${formatTime(event.pickup_end_time)}`
                    : formatTime(event.event_date)
                  }
                </p>
              </div>
              <div className="pt-3 border-t border-gray-200/50 dark:border-gray-700/50">
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Registration</p>
                <p className="text-foreground text-xs leading-relaxed">
                  Opens: {formatDate(event.registration_opens)} at {formatTime(event.registration_opens)}
                </p>
                <p className="text-foreground text-xs leading-relaxed mt-1">
                  Closes: {formatDate(event.registration_closes)} at {formatTime(event.registration_closes)}
                </p>
                <p className="text-muted-foreground text-xs italic mt-2">
                  (Registration closes 2 hours prior to the event for kitchen prep)
                </p>
              </div>
            </div>
          </div>

          {/* Capacity Card - Tiered Reveal System */}
          {event.max_lobster_tails && capacity && !capacity.is_unlimited ? (() => {
            const status = getCapacityStatus(
              capacity.percent_used,
              capacity.current_tail_count,
              capacity.remaining_capacity,
              calculatedStatus || undefined
            )

            return (
              <div className="bg-card text-card-foreground rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-center space-x-3 mb-6">
                  <span className="text-2xl">🦞</span>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Capacity</h3>
                </div>

                {/* Status Indicator - Tiered Messaging */}
                <div className={`rounded-lg p-4 mb-6 ${status.bgColor} ${status.textColor}`}>
                  <div className="text-center">
                    <div className="text-lg font-bold mb-1">
                      {status.icon} {status.heading}
                    </div>
                    <div className="text-sm font-normal">
                      {status.message}
                    </div>
                  </div>
                </div>

                {/* Tier 1 (Abundant): Show only context, no numbers */}
                {status.tier === 'abundant' && (
                  <div className="text-center py-2">
                    <p className="text-sm text-muted-foreground">
                      This exclusive pop-up serves only {event.max_lobster_tails} premium lobster tails.
                    </p>
                  </div>
                )}

                {/* Tier 2 (Selling Fast): Show social proof */}
                {status.tier === 'selling-fast' && status.showReservedCount && (
                  <div className="text-center py-2">
                    <p className="text-3xl font-bold text-foreground mb-2">
                      {capacity.current_tail_count}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Tails Reserved
                    </p>
                  </div>
                )}

                {/* Tier 3 & 4 (Low/Critical): Show progress bar and numbers */}
                {(status.tier === 'low' || status.tier === 'critical') && status.showProgressBar && (
                  <>
                    <div className="w-full bg-muted rounded-full h-3 overflow-hidden shadow-inner mb-4">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          status.tier === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                        }`}
                        style={{ width: `${Math.min(capacity.percent_used, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground mb-4">
                      <span className="font-semibold">{Math.round(capacity.percent_used)}% reserved</span>
                    </div>
                  </>
                )}

                {/* Show detailed numbers only in Tier 3+ */}
                {status.showNumbers && (
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Tails Reserved</p>
                      <p className="text-foreground font-bold text-base">{capacity.current_tail_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Total Available</p>
                      <p className="text-foreground font-bold text-base">{event.max_lobster_tails}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Remaining</p>
                      <p className="text-foreground font-bold text-base">{capacity.remaining_capacity}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })() : null}
        </div>


        {/* PRD Phase 1: Menu Section with Progressive Disclosure */}
        <div id="menu-section" className="space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-gray-100 flex items-center space-x-4 mb-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-xl">
                  <Star color="#7f1d1d" size={32} className="dark:text-secret-primary" />
                </div>
                <span>SECRET Menu</span>
              </h2>
              <p className="text-muted-foreground text-base md:text-lg font-medium">
                Exclusive items available only to SECRET members
              </p>
            </div>
          </div>

          {/* Menu with Lock Overlay for Scheduled State */}
          {calculatedStatus === 'Scheduled' ? (
            <div className="relative">
              <MenuGrid items={menuItems} loading={menuLoading} eventStatus={calculatedStatus} />
              {/* Lock Overlay - Progressive Disclosure */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90 backdrop-blur-md flex flex-col items-center justify-center pointer-events-none rounded-2xl border-2 border-gray-600/50">
                <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm mb-6">
                  <Lock className="w-20 h-20 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-white font-black text-2xl mb-3">Available when registration opens</p>
                <p className="text-gray-200 text-base font-medium max-w-md text-center px-4">
                  Opens {formatDate(event.registration_opens)} at {formatTime(event.registration_opens)}
                </p>
              </div>
            </div>
          ) : canPlaceOrders && hasAvailableSlots && hasCapacityAvailable ? (
            /* Active/In Progress: Menu Unlocked */
            <MenuGrid items={menuItems} loading={menuLoading} eventStatus={calculatedStatus} />
          ) : calculatedStatus === 'Completed' ? (
            /* Completed: Show menu but locked */
            <div className="relative">
              <MenuGrid items={menuItems} loading={menuLoading} eventStatus={calculatedStatus} />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90 backdrop-blur-md flex flex-col items-center justify-center pointer-events-none rounded-2xl border-2 border-gray-600/50">
                <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm mb-6">
                  <Lock className="w-20 h-20 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-white font-black text-2xl mb-3">This menu is no longer available</p>
                <p className="text-gray-200 text-base font-medium max-w-md text-center px-4">
                  This event took place on {formatDate(event.event_date)}
                </p>
              </div>
            </div>
          ) : (
            /* Other locked states */
            <div className="text-center py-20 bg-card rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
              <div className="p-4 bg-muted rounded-2xl w-fit mx-auto mb-6">
                <Lock className="w-16 h-16 text-muted-foreground" strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Menu Not Available</h3>
              <p className="text-muted-foreground text-base max-w-md mx-auto leading-relaxed px-4">
                {calculatedStatus === 'Enrollment Closed' && 'Registration period has ended'}
                {canPlaceOrders && !hasAvailableSlots && 'Event is fully booked'}
                {canPlaceOrders && hasAvailableSlots && !hasCapacityAvailable && 'All lobster tails have been reserved'}
              </p>
            </div>
          )}

          {/* Floating Cart Widget */}
          <FloatingCart eventId={eventId!} canPlaceOrders={canPlaceOrders} />
        </div>
      </div>
      )}
    </div>
  )
}

export default EventDetailsPage